
import { Fragment, useEffect, useState } from "react";
import { Tabs, Tab, Box, IconButton, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Menu } from '@mui/material';
import { Breadcrumbs, Btn } from "../../../../AbstractElements";
import { Container } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@progress/kendo-react-common";
import axios from "axios";
import { useAuth } from "oidc-react";
import { ShowError, ShowSucess, getUserRole } from "../../../../_helper/helper";
import { getFormalApiErrorMessage } from "../../../../utils/apiError";

const ReqCombinedTabs = () => {

    const history = useNavigate();
    const { id, positionId } = useParams();
    const { userData } = useAuth();
    const currentRole = getUserRole(userData);
    const [activeLeftTab, setActiveLeftTab] = useState(0);
    const [leftTabs, setLeftTabs] = useState([
        { id: 0, label: 'V1', activeTopTab: 0, topTabs: ['Education', 'Skill', 'Certification', 'Specialization'] }
    ]);
    const [tabQuestions, setTabQuestions] = useState({});
    const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
    const [positionIdMap, setPositionIdMap] = useState({});

    const fetchRequirements = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_DOTNET_URL}/Job_ANNOUNCEMENT/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("jwt_access_token")}`,
                    },
                }
            );

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const positions = response.data;
                const newLeftTabs = [];
                const newTabQuestions = {};
                const newPositionIdMap = {};

                positions.forEach((position, posIndex) => {
                    const versionLabel = `V${posIndex + 1}`;
                    newLeftTabs.push({
                        id: posIndex,
                        label: versionLabel,
                        activeTopTab: 0,
                        topTabs: ['Education', 'Skill', 'Certification', 'Specialization']
                    });

                    newPositionIdMap[posIndex] = position.positionId;

                    const groupedRequirements = {
                        'Education': [],
                        'Skill': [],
                        'Certification': [],
                        'Specialization': []
                    };

                    position.requirements?.forEach(req => {
                        const type = req.type.toLowerCase();
                        const sectionName = type.charAt(0).toUpperCase() + type.slice(1);
                        if (groupedRequirements[sectionName]) {
                            groupedRequirements[sectionName].push({
                                id: req.requirementId,
                                question: req.description,
                                answerType: req.answerType,
                                weight: req.defaultWeight
                            });
                        }
                    });

                    ['Education', 'Skill', 'Certification', 'Specialization'].forEach((section, sectionIndex) => {
                        const key = `${posIndex}-${sectionIndex}`;
                        newTabQuestions[key] = groupedRequirements[section].length > 0
                            ? groupedRequirements[section]
                            : [{ id: 0, question: '', answerType: 'yes-no', weight: '' }];
                    });
                });

                setLeftTabs(newLeftTabs);
                setTabQuestions(newTabQuestions);
                setPositionIdMap(newPositionIdMap);
            }
        } catch (error) {
            console.error('Error fetching requirements:', error);
        }
    };

    useEffect(() => {
        if (positionId) {
            fetchRequirements();
        }
    }, [positionId]);

    const addLeftTab = () => {
        const newTab = {
            id: leftTabs.length,
            label: `V${leftTabs.length + 1}`,
            activeTopTab: 0,
            topTabs: ['Education', 'Skill', 'Certification', 'Specialization']
        };
        setLeftTabs([...leftTabs, newTab]);
        ShowSucess('Tab added successfully!');
    };

    const getTabKey = (leftTabId, topTabIndex) => `${leftTabId}-${topTabIndex}`;

    const getQuestions = (leftTabId, topTabIndex) => {
        const key = getTabKey(leftTabId, topTabIndex);
        return tabQuestions[key] || [{ id: 0, question: '', answerType: 'yes-no', weight: '' }];
    };

    const setQuestions = (leftTabId, topTabIndex, questions) => {
        const key = getTabKey(leftTabId, topTabIndex);
        setTabQuestions(prev => ({ ...prev, [key]: questions }));
    };



    const updateTopTab = (leftTabId, topTabIndex) => {
        setLeftTabs(leftTabs.map(tab =>
            tab.id === leftTabId ? { ...tab, activeTopTab: topTabIndex } : tab
        ));
    };

    const addQuestion = (leftTabId, topTabIndex) => {
        const currentQuestions = getQuestions(leftTabId, topTabIndex);
        const newQuestions = [...currentQuestions, { id: currentQuestions.length, question: '', answerType: 'yes-no', weight: '' }];
        setQuestions(leftTabId, topTabIndex, newQuestions);
        ShowSucess('Question added successfully!');
    };

    const updateQuestion = (leftTabId, topTabIndex, id, field, value) => {
        const currentQuestions = getQuestions(leftTabId, topTabIndex);
        const updatedQuestions = currentQuestions.map(q => q.id === id ? { ...q, [field]: value } : q);
        setQuestions(leftTabId, topTabIndex, updatedQuestions);
    };

    const removeQuestion = (leftTabId, topTabIndex, id) => {
        const currentQuestions = getQuestions(leftTabId, topTabIndex);
        if (currentQuestions.length > 1) {
            const filteredQuestions = currentQuestions.filter(q => q.id !== id);
            setQuestions(leftTabId, topTabIndex, filteredQuestions);
        }
    };

    const getAvailableVersionsForCopy = () => {
        return leftTabs.filter(tab => {
            if (tab.id === currentLeftTab.id) return false; // Don't show current version
            // Check all sections for content
            return tab.topTabs.some((_, sectionIndex) => {
                const sectionQuestions = getQuestions(tab.id, sectionIndex);
                return sectionQuestions.some(q => q.question.trim() !== '');
            });
        });
    };

    const getAvailableSectionsForVersion = (sourceTabId) => {
        return leftTabs.find(tab => tab.id === sourceTabId)?.topTabs.filter((sectionName, sectionIndex) => {
            const sectionQuestions = getQuestions(sourceTabId, sectionIndex);
            return sectionQuestions.some(q => q.question.trim() !== '');
        }) || [];
    };

    const copyFromSection = (sourceTabId, sourceSectionIndex) => {
        const sourceQuestions = getQuestions(sourceTabId, sourceSectionIndex);
        const copiedQuestions = sourceQuestions.map((q, index) => ({
            ...q,
            id: index
        }));
        setQuestions(currentLeftTab.id, currentLeftTab.activeTopTab, copiedQuestions);
        setCopyMenuAnchor(null);
    };

    const handleCopyClick = (event) => {
        setCopyMenuAnchor(event.currentTarget);
    };

    const handleCopyClose = () => {
        setCopyMenuAnchor(null);
    };

    const saveAllData = async () => {
        let hasAnyQuestion = false;
        for (const tab of leftTabs) {
            for (let sectionIndex = 0; sectionIndex < tab.topTabs.length; sectionIndex++) {
                const questions = getQuestions(tab.id, sectionIndex);
                for (const q of questions) {
                    if (q.question.trim() === '') continue;
                    hasAnyQuestion = true;
                    if (q.weight !== '' && q.weight !== null && q.weight !== undefined) {
                        const weightNum = Number(q.weight);
                        if (isNaN(weightNum) || weightNum <= 0) {
                            ShowError(`Invalid weight for question "${q.question}" in ${tab.label} - ${tab.topTabs[sectionIndex]}. Weight must be a positive number.`);
                            return;
                        }
                    }
                }
            }
        }

        if (!hasAnyQuestion) {
            ShowError('Please add at least one question before saving.');
            return;
        }

        const saveData = {
            positions: leftTabs.map(tab => {
                const isV1WithBlankData = tab.id === 0 && tab.topTabs.every((_, sectionIndex) => {
                    const questions = getQuestions(tab.id, sectionIndex);
                    return questions.every(q => q.question.trim() === '');
                });

                return {
                    positionId: isV1WithBlankData ? 0 : (positionIdMap[tab.id] || 0),
                    positionNo: parseInt(id),
                    requirementVersion: tab.id === 0 ? 1.0 : parseFloat(`1.${tab.id + 1}`),
                    status: "Active",
                    requirements: tab.topTabs.flatMap((sectionName, sectionIndex) =>
                        getQuestions(tab.id, sectionIndex)
                            .filter(q => q.question.trim() !== '')
                            .map((q, qIndex) => ({
                                requirementId: q.question.trim() === '' ? 0 : (q.id || 0),
                                type: sectionName.toUpperCase(),
                                description: q.question,
                                answerType: q.answerType,
                                years: null,
                                defaultWeight: q.weight || "",
                                status: "Active"
                            }))
                    )
                };
            })
        };

        try {
            await axios.post(
                `${process.env.REACT_APP_API_DOTNET_URL}/Job_ANNOUNCEMENT/AddUpdateRequirement`,
                saveData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("jwt_access_token")}`,
                    },
                }
            );
            ShowSucess('Data saved successfully!');
            await fetchRequirements();
        } catch (error) {
            ShowError(getFormalApiErrorMessage(error, 'Error saving data'));
        }
    };

    const currentLeftTab = leftTabs[activeLeftTab];
    const currentQuestions = getQuestions(currentLeftTab.id, currentLeftTab.activeTopTab);

    return (

        <Fragment>
            <Container fluid>
                <Breadcrumbs
                    mainTitle={
                        <Typography variant="h5">
                            <Icon
                                style={{ fontSize: "25px", margin: "10px" }}
                                name="list-latin-small"
                            />
                            Requirements
                        </Typography>
                    }
                    parent={
                        <Link
                            to={`${process.env.PUBLIC_URL}/positions`}
                            className="font-light"
                        >
                            {positionId}
                        </Link>
                    }
                    title="Details"
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Btn
                                attrBtn={{
                                    color: "primary",
                                    onClick: () => history(`${process.env.PUBLIC_URL}/positions`),
                                    style: { marginRight: "10px" },
                                }}
                            >
                                <Icon style={{ fontSize: "18px" }} name="arrow-left" />{" "}
                                &nbsp;Back
                            </Btn>
                            <Typography variant="h5">
                                Position: {positionId}
                            </Typography>
                        </Box>
                        <Btn
                            attrBtn={{
                                color: "primary",
                                onClick: saveAllData,
                                disabled: !["Classification", "SUPER-USER"].includes(currentRole),
                                title: !["Classification", "SUPER-USER"].includes(currentRole) ? "Only Classification or Super User members can save requirements" : "",
                            }}
                        >
                            <Icon style={{ fontSize: "18px" }} name="save" />{" "}
                            &nbsp;Save All Requirements
                        </Btn>
                    </Box>
                    <Box sx={{ display: 'flex', padding: 2, gap: 2, border: '1px solid #ccc' }}>
                        <Box sx={{ width: 200, borderRight: 1, borderColor: 'divider' }}>
                            <Tabs value={activeLeftTab} onChange={(e, v) => setActiveLeftTab(v)} orientation="vertical">
                                {leftTabs.map(tab => (
                                    <Tab key={tab.id} label={tab.label} />
                                ))}
                            </Tabs>
                            <Btn
                                attrBtn={{
                                    color: "primary",
                                    outline: true,
                                    size: "sm",
                                    onClick: addLeftTab,
                                    style: { margin: "8px" },
                                }}
                            >
                                <Icon style={{ fontSize: "16px" }} name="plus" />{" "}
                                &nbsp;Add Tab
                            </Btn>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Tabs value={currentLeftTab.activeTopTab} onChange={(e, v) => updateTopTab(currentLeftTab.id, v)} variant="filled">
                                {currentLeftTab.topTabs.map((tabLabel, index) => (
                                    <Tab key={index} label={tabLabel} />
                                ))}
                            </Tabs>
                            <Box sx={{ p: 2.5 }}>
                                {currentQuestions.map((q, index) => (
                                    <Box key={q.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <TextField
                                            label={`Question ${index + 1}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{ flex: 1 }}
                                            value={q.question}
                                            onChange={(e) => updateQuestion(currentLeftTab.id, currentLeftTab.activeTopTab, q.id, 'question', e.target.value)}
                                            placeholder="Enter your question here..."
                                        />
                                        <TextField
                                            label="Default Weight"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            sx={{ width: 120 }}
                                            value={q.weight || ''}
                                            onChange={(e) => updateQuestion(currentLeftTab.id, currentLeftTab.activeTopTab, q.id, 'weight', e.target.value)}
                                            inputProps={{ min: 1 }}
                                            placeholder="Optional"
                                        />
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <InputLabel>Answer Type</InputLabel>
                                            <Select
                                                value={q.answerType}
                                                onChange={(e) => updateQuestion(currentLeftTab.id, currentLeftTab.activeTopTab, q.id, 'answerType', e.target.value)}
                                                label="Answer Type"
                                            >
                                                <MenuItem value="yes-no">Yes/No</MenuItem>
                                                <MenuItem value="input">Input</MenuItem>
                                            </Select>
                                        </FormControl>
                                        {currentQuestions.length > 1 && (
                                            <IconButton
                                                size="small"
                                                title="Delete"
                                                onClick={() => removeQuestion(currentLeftTab.id, currentLeftTab.activeTopTab, q.id)}
                                                style={{ color: '#d32f2f', padding: '2px' }}
                                            >
                                                <Icon name="delete" style={{ fontSize: '16px' }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Btn
                                        attrBtn={{
                                            color: "primary",
                                            outline: true,
                                            size: "sm",
                                            onClick: () => addQuestion(currentLeftTab.id, currentLeftTab.activeTopTab),
                                        }}
                                    >
                                        <Icon style={{ fontSize: "16px" }} name="plus" />{" "}
                                        &nbsp;Add Question
                                    </Btn>
                                    {currentLeftTab.id > 0 && getAvailableVersionsForCopy().length > 0 && (
                                        <>
                                            <Btn
                                                attrBtn={{
                                                    color: "secondary",
                                                    size: "sm",
                                                    onClick: handleCopyClick,
                                                }}
                                            >
                                                <Icon style={{ fontSize: "16px" }} name="copy" />{" "}
                                                &nbsp;Copy Questions
                                            </Btn>
                                            <Menu
                                                anchorEl={copyMenuAnchor}
                                                open={Boolean(copyMenuAnchor)}
                                                onClose={handleCopyClose}
                                            >
                                                {getAvailableVersionsForCopy().map(tab =>
                                                    getAvailableSectionsForVersion(tab.id).map((sectionName, sectionIndex) => {
                                                        const actualSectionIndex = leftTabs.find(t => t.id === tab.id)?.topTabs.indexOf(sectionName);
                                                        return (
                                                            <MenuItem
                                                                key={`${tab.id}-${actualSectionIndex}`}
                                                                onClick={() => copyFromSection(tab.id, actualSectionIndex)}
                                                            >
                                                                Copy from {tab.label} {sectionName}
                                                            </MenuItem>
                                                        );
                                                    })
                                                ).flat()}
                                            </Menu>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Fragment>
    );
};

export default ReqCombinedTabs;